import { getStaffSessionToken } from '../modules/auth/staffSession';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { authSelector, setAuthToken } from '../modules/auth/state/authSlice';
import {
  useGetUserDataQuery,
  useTeamSearchUsersMutation,
  useTeamSwitchUserMutation
} from '../modules/auth/api/authApi';
import { useAppDispatch } from '../store';
import { api } from '../api/api';

/**
 * Team-only floating header that shows the currently-logged-in customer
 * email and lets admins jump to another account without re-entering the
 * admin password. The admin password is pulled from sessionStorage (set
 * during the initial team-login).
 */
export const AdminUserSwitcher = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAdmin, token } = useSelector(authSelector);
  const { data: userData } = useGetUserDataQuery({ token: token || '' }, { skip: !token });
  const [teamSearchUsers, { isLoading: isSearching }] = useTeamSearchUsersMutation();
  const [teamSwitchUser, { isLoading: isSwitching }] = useTeamSwitchUserMutation();

  type SearchResult = {
    email: string;
    matchedVia: 'email' | 'iccid' | 'stripe';
    matchedValue: string;
  };

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const currentEmail = userData?.data?.email;
  const staffSession = useMemo(() => getStaffSessionToken() || '', []);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!isAdmin) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }
    if (!staffSession) return;
    const handle = setTimeout(async () => {
      try {
        const res = await teamSearchUsers({ query: trimmed }).unwrap();
        setResults(res.results || []);
        setIsOpen(true);
      } catch (err) {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, isAdmin, staffSession, teamSearchUsers]);

  if (!isAdmin) return null;

  const handleSelect = async (email: string) => {
    if (!staffSession) {
      toast('Admin session expired, please login again', { type: 'error' });
      return;
    }
    if (email === currentEmail) {
      setIsOpen(false);
      return;
    }
    try {
      const res = await teamSwitchUser({ email }).unwrap();
      if (res?.token) {
        dispatch(setAuthToken(res.token));
        dispatch(api.util.resetApiState());
        setQuery('');
        setResults([]);
        setIsOpen(false);
        toast(`Switched to ${email}`, { type: 'success' });
        navigate('/dashboard');
      }
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message || 'Failed to switch user';
      toast(message, { type: 'error' });
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      <CurrentLabel>
        <Dot />
        Admin · <strong>{currentEmail || 'loading…'}</strong>
      </CurrentLabel>
      <SearchBox>
        <SearchInput
          value={query}
          placeholder="Search by email, ICCID or Stripe ID…"
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          disabled={isSwitching}
        />
        {(isOpen || isSearching) && query.trim().length >= 2 && (
          <Dropdown>
            {isSearching && <DropdownEmpty>Searching…</DropdownEmpty>}
            {!isSearching && results.length === 0 && (
              <DropdownEmpty>No matches</DropdownEmpty>
            )}
            {!isSearching &&
              results.map((r) => {
                const isCurrent = r.email === currentEmail;
                const viaLabel =
                  r.matchedVia === 'iccid'
                    ? `via ICCID ${r.matchedValue}`
                    : r.matchedVia === 'stripe'
                      ? `via Stripe ${r.matchedValue}`
                      : null;
                return (
                  <DropdownItem
                    key={`${r.email}-${r.matchedVia}`}
                    isCurrent={isCurrent}
                    onClick={() => handleSelect(r.email)}>
                    <ItemText>
                      <ItemEmail>{r.email}</ItemEmail>
                      {viaLabel && <ItemHint>{viaLabel}</ItemHint>}
                    </ItemText>
                    {isCurrent && <CurrentTag>current</CurrentTag>}
                  </DropdownItem>
                );
              })}
          </Dropdown>
        )}
      </SearchBox>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: fixed;
  top: 10px;
  right: 10px;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.4rem;
  background: white;
  color: #1f2937;
  padding: 0.4rem 0.6rem;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  font-size: 0.8rem;
  width: 20rem;
  max-width: calc(100vw - 20px);

  @media (max-width: 850px) {
    width: 18rem;
    /* Clear the top nav bar on mobile instead of overlapping it */
    top: 70px;
  }
`;

const CurrentLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #6b7280;
  font-size: 0.75rem;
  strong {
    color: #29a8d3;
    font-weight: 600;
    word-break: break-all;
  }
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
`;

const SearchBox = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.4rem 0.6rem;
  font-size: 0.8rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #f9fafb;
  color: #1f2937;
  box-sizing: border-box;
  &::placeholder {
    color: #9ca3af;
  }
  &:focus {
    outline: none;
    border-color: #29a8d3;
    background: white;
    box-shadow: 0 0 0 3px rgba(41, 168, 211, 0.15);
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  color: #1f2937;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  max-height: 240px;
  overflow-y: auto;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
`;

const DropdownItem = styled.div<{ isCurrent: boolean }>`
  padding: 0.45rem 0.6rem;
  cursor: ${({ isCurrent }) => (isCurrent ? 'default' : 'pointer')};
  font-size: 0.8rem;
  background: ${({ isCurrent }) => (isCurrent ? '#f3f4f6' : 'white')};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  word-break: break-all;
  &:hover {
    background: ${({ isCurrent }) => (isCurrent ? '#f3f4f6' : '#e6f6fb')};
  }
  & + & {
    border-top: 1px solid #f1f5f9;
  }
`;

const CurrentTag = styled.span`
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: #29a8d3;
  color: white;
  padding: 1px 6px;
  border-radius: 999px;
  flex-shrink: 0;
`;

const ItemText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
`;

const ItemEmail = styled.span`
  color: #1f2937;
  font-size: 0.8rem;
  word-break: break-all;
`;

const ItemHint = styled.span`
  color: #6b7280;
  font-size: 0.65rem;
  word-break: break-all;
`;

const DropdownEmpty = styled.div`
  padding: 0.5rem 0.6rem;
  font-size: 0.8rem;
  color: #6b7280;
`;
